import type { ColorSource } from 'pixi.js';
import { Container } from 'pixi.js';
import { Signal } from 'typed-signals';
import type { TileStackModel } from '../../models/board/TileStackModel';
import { TileStackShadow } from './TileStackShadow';
import { TileView, TILE_Z_OFFSET } from './TileView';
import type { BlockerView } from '../../types';
import { createStackHintMask } from './createStackHintMask';
import { TileStackDebugView } from './TileStackDebugView';
import { TileStackViewHighlight } from './TileStackViewHighlight';

export class TileStackView extends Container {
  public static addDebugView = false;

  public readonly onTilePreparedForMatchRemoval = new Signal();

  public readonly shadow: TileStackShadow = new TileStackShadow().props({
    parent: this,
  });

  public readonly tiles = new Container<TileView>().props({
    parent: this,
  });

  public readonly highlight = new TileStackViewHighlight(this);

  constructor(public readonly model: TileStackModel) {
    super();

    this.tiles.on('childAdded', () => {
      this.updateTiles(this.tiles.children.length);
    });
    this.tiles.on('childRemoved', () => {
      this.updateTiles(this.tiles.children.length);
    });

    if (TileStackView.addDebugView) {
      void new TileStackDebugView(this);
    }
  }

  public getTileView(tileId: string): TileView | null {
    return this.tiles.children.find((tileView) => tileView.model?.id === tileId) ?? null;
  }

  public addTileView(tileView: TileView | TileView[]): this {
    if (Array.isArray(tileView)) {
      tileView.forEach((view) => this.addTileView(view));
      return this;
    }

    tileView.position.set(0, this.getVisibleZOffset());

    this.tiles.addChild(tileView);
    this.updateTiles(this.tiles.children.length);
    return this;
  }

  public reset(): void {
    if (this.destroyed) return;
    const { tiles, shadow } = this;
    tiles.position.set(0, 0);
    if (shadow) this.addChild(shadow);
    this.addChild(tiles);
    this.destroyMask();
    this.tintTiles(0xffffff);
  }

  public removeTile(tileView: TileView): void {
    this.tiles.removeChild(tileView);
    this.updateTiles(this.tiles.children.length);
  }

  public updateTiles(tileCount: number): void {
    this.shadow?.update(tileCount);
  }

  public applyHintMask(): void {
    const tileCount = this.model.getTileCount();
    this.mask = createStackHintMask(tileCount).props({
      y: this.getLocalBounds().maxY,
      parent: this,
    });
  }

  public destroyMask(): void {
    if (this.mask instanceof Container) this.mask?.destroy();
    this.mask = null;
  }

  public tintTiles(color: ColorSource): void {
    this.tiles.children.forEach((tile) => {
      if (!(tile instanceof TileView)) return;
      tile.tint = color;
    });
  }

  public animateBounce(): void {
    this.tiles.y = -7;
    this.tiles.tweenTo({ y: 0 }, 400, 'bounceOut', true);
  }

  public fadeIn(duration = 200): void {
    this.alpha = 0;
    this.tweenTo({ alpha: 1 }, duration, 'quadOut', true);
  }

  public fadeOut(duration = 200): void {
    this.tweenTo({ alpha: 0 }, duration, 'quadOut', true);
  }

  public getNextTileZOffset(): number {
    return -this.model.getTiles().length * TILE_Z_OFFSET;
  }

  public getVisibleZOffset(): number {
    return -this.tiles.children.length * TILE_Z_OFFSET;
  }

  public addBlockerView(view: BlockerView): this {
    view.props({
      parent: this,
      addAbove: view.getModel().underTile ? this.tiles : undefined,
      y: this.getVisibleZOffset(),
    });

    view?.setStackView?.(this);
    return this;
  }

  public prepareTilesForRemoval(tilesIds: string[]): TileView[] {
    const views = tilesIds
      .map((tileId) => {
        const tileView = this.getTileView(tileId);
        if (!tileView) return null;
        return tileView.props({
          parent: this,
        });
      })
      .filter(Boolean)
      .reverse() as TileView[];

    this.onTilePreparedForMatchRemoval.emit();

    return views;
  }
}
